/**
 * Page building blocks on top of shadcn/ui. Pages compose these so every screen has
 * the same rhythm: PageHeader → (StatGrid) → FilterBar → Section/DataTable.
 */

import { ArrowDown, ArrowRight, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, type LucideIcon } from 'lucide-react';
import { Fragment, useId, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { TONE_CLASS, TONE_ICON, type Tone } from './Badge';
import { EmptyState } from './States';

/** A titled card. `flush` removes the body padding (for tables that run edge to edge). */
export function Section({
  id,
  title,
  description,
  actions,
  children,
  flush = false,
  className,
}: {
  /** Anchor (`#id`) a link can scroll to. */
  id?: string;
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
  flush?: boolean;
  className?: string;
}) {
  return (
    <Card id={id} className={cn('scroll-mt-20 gap-4 rounded-xl py-5 shadow-none', flush && 'pb-0', className)}>
      {title || actions ? (
        <CardHeader className="px-5">
          {title ? <CardTitle className="text-[15px] leading-snug">{title}</CardTitle> : null}
          {description ? <CardDescription className="text-[13px]">{description}</CardDescription> : null}
          {actions ? <CardAction className="flex flex-wrap items-center gap-2">{actions}</CardAction> : null}
        </CardHeader>
      ) : null}
      <CardContent className={cn(flush ? 'px-0' : 'px-5')}>{children}</CardContent>
    </Card>
  );
}

const STAT_ACCENT: Record<Tone, string> = {
  neutral: 'border-t-border',
  info: 'border-t-info',
  ok: 'border-t-success',
  pending: 'border-t-info',
  warn: 'border-t-warning',
  serious: 'border-t-serious',
  crit: 'border-t-critical',
};

/** A KPI tile: label, big value, optional status, hint and a link to the page that explains it. */
export function StatCard({
  label,
  value,
  tone = 'neutral',
  status,
  hint,
  icon: Icon,
  to,
  linkLabel,
  children,
}: {
  label: ReactNode;
  value: ReactNode;
  tone?: Tone;
  status?: ReactNode;
  hint?: ReactNode;
  icon?: LucideIcon;
  to?: string;
  linkLabel?: ReactNode;
  children?: ReactNode;
}) {
  const StatusIcon = TONE_ICON[tone];
  // The reference layouts' metric tile: label and icon on top, a large figure, a short hint.
  return (
    <Card className={cn('gap-2 rounded-xl py-4 shadow-none', tone !== 'neutral' && cn('border-t-2', STAT_ACCENT[tone]))}>
      <CardHeader className="gap-3 px-5">
        <CardDescription className="flex items-center justify-between gap-2 text-[13px] text-muted-foreground">
          <span className="min-w-0">{label}</span>
          {Icon ? <Icon className="size-4 shrink-0 text-muted-foreground/70" aria-hidden="true" /> : null}
        </CardDescription>
        <CardTitle className="tabular text-[28px] leading-tight font-semibold tracking-tight">{value}</CardTitle>
        {status ? (
          <span className={cn('inline-flex w-fit items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium', TONE_CLASS[tone])}>
            {StatusIcon ? <StatusIcon className="size-3" aria-hidden="true" /> : null}
            {status}
          </span>
        ) : null}
      </CardHeader>
      {hint || children || to ? (
        <CardContent className={cn('flex flex-1 flex-col gap-2 px-5 text-xs', tone === 'warn' || tone === 'serious' ? 'text-warning' : tone === 'crit' ? 'text-critical' : 'text-muted-foreground')}>
          {hint ? <div>{hint}</div> : null}
          {children}
          {to ? (
            <Link to={to} className="mt-auto inline-flex items-center gap-1 pt-1 text-[13px] font-medium text-primary hover:underline">
              {linkLabel} <ArrowRight className="size-3.5" aria-hidden="true" />
            </Link>
          ) : null}
        </CardContent>
      ) : null}
    </Card>
  );
}

/** Responsive grid for StatCards. */
export function StatGrid({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4', className)}>{children}</div>;
}

/** Toolbar row for filters above a table; put Inputs/Selects inside, `actions` go right. */
export function FilterBar({ children, actions, className }: { children: ReactNode; actions?: ReactNode; className?: string }) {
  return (
    <div className={cn('mb-4 flex flex-wrap items-end gap-3', className)}>
      <div className="flex flex-1 flex-wrap items-end gap-3">{children}</div>
      {actions ? <div className="flex items-center gap-2">{actions}</div> : null}
    </div>
  );
}

/** Label + control stacked, for FilterBar and forms. */
/**
 * A labelled form field. The default wraps one control in a `<label>`. `group`: a widget of
 * several buttons (a search with results, a picked value with "Chọn") is a labelled group
 * instead — a `<label>` passes every click inside it on to its first button or input, so a
 * click that swaps the widget's content (a result picked) would land on the new first button
 * ("Chọn") and undo itself.
 */
export function Field({ label, children, className, hint, group }: { label: ReactNode; children: ReactNode; className?: string; hint?: ReactNode; group?: boolean }) {
  const id = useId();
  if (group) {
    return (
      <div role="group" aria-labelledby={id} className={cn('grid gap-1.5 text-sm', className)}>
        <span id={id} className="text-xs font-medium text-muted-foreground">
          {label}
        </span>
        {children}
        {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
      </div>
    );
  }
  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control -- the control is the child
    <label className={cn('grid gap-1.5 text-sm', className)}>
      <span className="text-xs font-medium text-muted-foreground">{label}</span>
      {children}
      {hint ? <span className="text-xs text-muted-foreground">{hint}</span> : null}
    </label>
  );
}

export interface Column<T> {
  key: string;
  header: ReactNode;
  cell: (row: T) => ReactNode;
  /** Right-aligned, tabular numbers. */
  numeric?: boolean;
  className?: string;
  /** With DataTable `onSort`: the header becomes a sort button sending this key. */
  sortKey?: string;
}

/**
 * A plain data table: header, rows, empty state. Keep business logic in the page;
 * this only renders. Wrap it in <Section flush> for the card look.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  empty,
  onRowClick,
  rowClassName,
  className,
  expand,
  sort,
  onSort,
}: {
  columns: Column<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  empty?: ReactNode;
  onRowClick?: (row: T) => void;
  /** Extra classes per row (e.g. the keyboard-selected one). */
  rowClassName?: (row: T) => string | undefined;
  className?: string;
  /** Full-width content under each row (e.g. an expander with the row's details). */
  expand?: (row: T) => ReactNode;
  /** The sort in effect (server-side); shown on the column whose `sortKey` matches. */
  sort?: { key: string; dir: 'asc' | 'desc' };
  /** Header click on a column with a `sortKey`; the page decides the next order. */
  onSort?: (key: string) => void;
}) {
  const { t } = useTranslation();
  if (rows.length === 0) return <div className="px-5 pb-5">{empty ?? <EmptyState title={t('table.noResults')} />}</div>;
  return (
    <div className={cn('overflow-x-auto', className)}>
      <Table>
        <TableHeader>
          <TableRow className="bg-muted/50 hover:bg-muted/50">
            {columns.map((c) => {
              const sortKey = onSort ? c.sortKey : undefined;
              const dir = sortKey !== undefined && sort?.key === sortKey ? sort.dir : undefined;
              const SortIcon = dir === 'asc' ? ArrowUp : dir === 'desc' ? ArrowDown : ArrowUpDown;
              return (
                <TableHead
                  key={c.key}
                  aria-sort={sortKey === undefined ? undefined : dir === 'asc' ? 'ascending' : dir === 'desc' ? 'descending' : 'none'}
                  className={cn('h-10 px-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground', c.numeric && 'text-right', c.className)}
                >
                  {sortKey === undefined ? (
                    c.header
                  ) : (
                    <button
                      type="button"
                      onClick={() => onSort?.(sortKey)}
                      className={cn('inline-flex items-center gap-1 rounded-sm uppercase hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none', dir && 'text-foreground')}
                    >
                      {c.header}
                      <SortIcon className={cn('size-3 shrink-0', !dir && 'opacity-40')} aria-hidden="true" />
                    </button>
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <Fragment key={rowKey(row)}>
              <TableRow className={cn(onRowClick && 'cursor-pointer', expand && 'border-b-0', rowClassName?.(row))} onClick={onRowClick ? () => onRowClick(row) : undefined}>
                {columns.map((c) => (
                  <TableCell key={c.key} className={cn('px-4 py-2.5 align-top', c.numeric && 'tabular text-right', c.className)}>
                    {c.cell(row)}
                  </TableCell>
                ))}
              </TableRow>
              {expand ? (
                <TableRow className="hover:bg-transparent">
                  <TableCell colSpan={columns.length} className="px-4 pt-0 pb-3 whitespace-normal">
                    {/* Zero width, full cell: wide content scrolls inside instead of widening the table. */}
                    <div className="w-0 min-w-full">{expand(row)}</div>
                  </TableCell>
                </TableRow>
              ) : null}
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

/** Previous/next paging footer for keyset-paged lists. */
export function Pager({
  onPrevious,
  onNext,
  canPrevious,
  canNext,
  info,
  previousLabel,
  nextLabel,
}: {
  onPrevious?: () => void;
  onNext?: () => void;
  canPrevious: boolean;
  canNext: boolean;
  info?: ReactNode;
  previousLabel?: ReactNode;
  nextLabel?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between gap-3 border-t px-4 py-3 text-sm text-muted-foreground">
      <div>{info}</div>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onPrevious} disabled={!canPrevious}>
          <ChevronLeft /> {previousLabel ?? t('action.previous')}
        </Button>
        <Button variant="outline" size="sm" onClick={onNext} disabled={!canNext}>
          {nextLabel ?? t('action.next')} <ChevronRight />
        </Button>
      </div>
    </div>
  );
}

/** Callout for notes, limitations and warnings inside a page. */
export function Callout({ tone = 'info', title, children, className }: { tone?: Tone; title?: ReactNode; children?: ReactNode; className?: string }) {
  const Icon = TONE_ICON[tone];
  return (
    <div role="note" className={cn('flex gap-3 rounded-lg border p-3 text-sm', TONE_CLASS[tone], className)}>
      {Icon ? <Icon className="mt-0.5 size-4 shrink-0" aria-hidden="true" /> : null}
      <div className="space-y-1 text-foreground">
        {title ? <div className="font-semibold">{title}</div> : null}
        {children ? <div className="text-foreground/85">{children}</div> : null}
      </div>
    </div>
  );
}
